import { createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../api/apiClient';
import { updateApplicationField, setNextStep, setSubmitting } from '../Slice/unifiedFormSlice';
import { toast } from 'react-toastify';

interface SubmitApplicationPayload {
  publisher_id: string;
  application_id: string;
  description: string;
  en_description: string;
  fa_description: string;
  subtitle_fa: string;
  subtitle_en: string;
  iconFile: File | null;
  imageFiles: File[];
}

interface ApplicationResponse {
  application: any;
  'current-step'?: number;
  'next-step'?: number;
  data: {
    id?: string;
    description?: string;
    en_description?: string;
    fa_description?: string;
    [key: string]: any;
  };
}

/**
 * Submits application form data (descriptions and files) to the server.
 * @param payload - Form data including descriptions and files
 * @returns Server response with application data and steps
 * @throws Error message if the request fails
 */
export const submitApplicationForm = createAsyncThunk<
  ApplicationResponse,
  SubmitApplicationPayload,
  { rejectValue: string }
>(
  'application/submitApplicationForm',
  async (payload, { dispatch, rejectWithValue }) => {
    if (!payload.application_id || !payload.publisher_id) {
      const errorMessage = 'شناسه اپلیکیشن یا ناشر نامعتبر است';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }

    dispatch(setSubmitting(true));
    try {
      const formData = new FormData();
      formData.append('publisher_id', payload.publisher_id);
      formData.append('application_id', payload.application_id);
      formData.append('description', payload.description);
      formData.append('en_description', payload.en_description);
      formData.append('fa_description', payload.fa_description);
      formData.append('subtitle_fa', payload.subtitle_fa);
      formData.append('subtitle_en', payload.subtitle_en);
      if (payload.iconFile) {
        formData.append('logo', payload.iconFile);
      }
      payload.imageFiles.forEach((file, index) => {
        formData.append(`screen_shots[${index}]`, file);
      });

      const response = await apiClient.post(
        `/v2/publisher/applications/createExtraInformation?publisher_id=${payload.publisher_id}&application_id=${payload.application_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json',
          },
        },
      );

      // Update Redux store with description fields
      dispatch(updateApplicationField({ field: 'description', value: payload.description }));
      dispatch(updateApplicationField({ field: 'en_description', value: payload.en_description }));
      dispatch(updateApplicationField({ field: 'fa_description', value: payload.fa_description }));
      dispatch(setNextStep(response.data?.['next-step'] || null));

      return response.data as ApplicationResponse;
    } catch (error: any) {
      console.error('Error in submitApplicationForm:', error);
      
      // The api client interceptor already unwrapped the response body, so
      // this is {message, errors} rather than an axios error.
      if (error?.errors || error?.message) {
        return rejectWithValue(error);
      }

      return rejectWithValue('خطا در ارسال اطلاعات');
    } finally {
      dispatch(setSubmitting(false));
    }
  },
);